import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError } from "../utils/errorHandler";
import {
  Box,
  Container,
  Tooltip,
  Typography,
  TextField,
  Button,
  Switch,
  Menu,
  FormControl,
  MenuItem,
  Popover,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

function LogicalGroups() {
  const [formData, setFormData] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    lg_id: "",
    name: "",
    client_specific_name: "",
  });
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteLgId, setDeleteLgId] = useState(null);
  const [deleteEnvId, setDeleteEnvId] = useState(null);
  const [deleteAppId, setDeleteAppId] = useState(null);

  // const [logicalGroups, setLogicalGroups] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false); // Add loading state
  const [searchTerm, setSearchTerm] = useState("");

  const [visibleColumns, setVisibleColumns] = useState({
    client_name: true,
    environment_name: true,
    application_name: true,
    lg_id: true,
    name: true,
    client_specific_name: true,
  });
  const [filters, setFilters] = useState({
    client_name: "",
    environment_name: "",
    application_name: "",
    lg_id: "",
    name: "",
    client_specific_name: "",
  });

  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [tempFilters, setTempFilters] = useState({ ...filters });

  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const fetchClients = async () => {
    try {
      const data = await apiService.get("/logical-groups/clients");
      setClients(data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchEnvironments = async () => {
    try {
      const data = await apiService.get("/logical-groups/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Error fetching environments:", err);
    }
  };

  const fetchApplications = async (envId = null) => {
    try {
      let url = "/logical-groups/applications";
      if (envId) {
        url += `?env_id=${envId}`;
      }
      const data = await apiService.get(url);
      setApplications(data);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setApplications([]);
    }
  };

  const fetchLogicalGroups = useCallback(async () => {
    setLoading(true); // Start loading
    const params = new URLSearchParams({ page, limit: rowsPerPage });

    // Add search term if present
    if (searchTerm.trim() !== "") {
      params.append("search", searchTerm);
    }

    // Add column-specific filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim() !== "") {
        params.append(key, value);
      }
    });

    try {
      const result = await apiService.get(
        `/logical-groups/paginate/list?${params.toString()}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false); // End loading
    }
  }, [page, rowsPerPage, filters, searchTerm]);

  useEffect(() => {
    fetchClients();
    fetchEnvironments();
    fetchApplications(); // Load all applications initially
    fetchLogicalGroups();
  }, [fetchLogicalGroups]);

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id && !isEditing) {
      const first = clients[0];
      setFormData((prev) => ({
        ...prev,
        cl_id: first.cl_id,
        client_name: first.name || "",
      }));
    }
  }, [clients, isEditing]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged =
        JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);
  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    lg_id: "Groups ID",
    name: "Logical Group",
    client_specific_name: "Client Name",
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? (checked ? 1 : 0) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleEnvironmentChange = (event, newValue) => {
    const envId = newValue ? newValue.env_id : "";
    setFormData((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Reset application when environment changes
    }));
    setFormErrors((prev) => ({ ...prev, env_id: "" }));

    // Fetch applications for the selected environment
    if (envId) {
      fetchApplications(envId);
    } else {
      setApplications([]); // Clear applications if no environment selected
    }
  };

  const validate = () => {
    const errors = {};

    // Trim and validate
    const trimmedLgId = formData.lg_id.trim();
    const trimmedName = formData.name.trim();
    const trimmedClientName = formData.client_specific_name?.trim() || "";

    if (!formData.cl_id.trim()) errors.cl_id = "Client is required.";
    if (!formData.env_id.trim()) errors.env_id = "Environment is required.";
    if (!formData.app_id.trim()) errors.app_id = "Application is required.";

    // Logical Group ID validation
    if (!trimmedLgId) {
      errors.lg_id = "Logical Group ID is required.";
    } else if (!/[a-zA-Z]/.test(trimmedLgId)) {
      errors.lg_id = "Must contain at least one letter.";
    } else if (/^[^a-zA-Z0-9]+$/.test(trimmedLgId)) {
      errors.lg_id = "Cannot contain only special characters.";
    }

    // Name validation
    if (!trimmedName) {
      errors.name = "Name is required.";
    } else if (!/[a-zA-Z]/.test(trimmedName)) {
      errors.name = "Must contain at least one letter.";
    } else if (/^[^a-zA-Z0-9]+$/.test(trimmedName)) {
      errors.name = "Cannot contain only special characters.";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Trim all string values
    const trimmedData = {
      ...formData,
      lg_id: formData.lg_id.trim(),
      name: formData.name.trim(),
      client_specific_name: formData.client_specific_name?.trim() || "",
    };

    try {
      // Check for duplicate based on client, environment, application, and lg_id when adding
      if (!isEditing) {
        const duplicate = data.some(
          (lg) => lg.cl_id === trimmedData.cl_id &&
                  lg.env_id === trimmedData.env_id && 
                  lg.app_id === trimmedData.app_id &&
                  lg.lg_id === trimmedData.lg_id
        );
        if (duplicate) {
          setFormErrors({ lg_id: "Logical Group ID already exists for this client, environment, and application combination." });
          return;
        }
      }

      if (isEditing) {
        await apiService.put(
          `/logical-groups/${trimmedData.lg_id}`,
          trimmedData,
        );
      } else {
        await apiService.post("/logical-groups", trimmedData);
      }

      setSuccessMessage(
        isEditing
          ? "Logical Group updated successfully!"
          : "Logical Group created successfully!",
      );

      // Reset form to initial state
      let initialClient = {};
      if (clients.length > 0) {
        const first = clients[0];
        initialClient = {
          cl_id: first.cl_id,
          client_name: first.name || "",
        };
      }

      setFormData({
        ...initialClient,
        env_id: "",
        app_id: "",
        lg_id: "",
        name: "",
        client_specific_name: "",
      });
      setFormErrors({});
      setIsEditing(false);
      setApplications([]); // Clear applications
      fetchLogicalGroups();
      setOpenDialog(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving logical group:", error);
      
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(error, "Logical Group")
        : handleCreateError(error, "Logical Group");
      
      // Handle specific duplicate error for form field
      if (error.response?.status === 409 || error.response?.status === 400) {
        setFormErrors({
          lg_id: error.response.data.message || errorMessage,
        });
      } else {
        setErrorMessage(errorMessage);
        setTimeout(() => setErrorMessage(""), 3000);
      }
    }
  };

  const handleEdit = (lg) => {
    setOriginalFormData({ ...lg });
    setFormData({
      ...lg,
      client_name: lg.client_name, // Preserve client name for display
    });
    setIsEditing(true);
    setIsFormChanged(false);
    setFormErrors({});

    // Load applications for the selected environment
    if (lg.env_id) {
      fetchApplications(lg.env_id);
    }

    setOpenDialog(true);
  };

  const handleDeleteClick = (lg_id, env_id, app_id) => {
    setDeleteLgId(lg_id);
    setDeleteEnvId(env_id);
    setDeleteAppId(app_id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteLgId) return;

    try {
      await apiService.delete(
        `/logical-groups/${deleteLgId}?env_id=${deleteEnvId}&app_id=${deleteAppId}`,
      );

      setSuccessMessage("Logical group deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);

      fetchLogicalGroups();
      setOpenDeleteDialog(false);
      setDeleteLgId(null);
      setDeleteEnvId(null);
      setDeleteAppId(null);
    } catch (err) {
      setOpenDeleteDialog(false);
      setDeleteLgId(null);
      setDeleteEnvId(null);
      setDeleteAppId(null);
      
      // Use centralized error handler
      const errorMessage = handleDeleteError(err, "Logical Group");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteLgId(null);
    setDeleteEnvId(null);
    setDeleteAppId(null);
  };

  const handleDialogOpen = () => {
    let initialClient = {};
    if (clients.length > 0) {
      const first = clients[0];
      initialClient = {
        cl_id: first.cl_id,
        client_name: first.name || "",
      };
    }

    setFormData({
      ...initialClient,
      env_id: "",
      app_id: "",
      lg_id: "",
      name: "",
      client_specific_name: "",
    });
    setFormErrors({});
    setIsEditing(false);
    setApplications([]); // Clear applications when opening new form
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    // Reset form data and errors when closing dialog
    setFormData({
      cl_id: "",
      env_id: "",
      app_id: "",
      lg_id: "",
      name: "",
      client_specific_name: "",
    });
    setFormErrors({});
    setIsEditing(false);
    setApplications([]); // Clear applications
    setOpenDialog(false);
    setOriginalFormData({});
    setIsFormChanged(false);
  };
  const columns = [
    "client_name",
    "environment_name",
    "application_name",
    "lg_id",
    "name",
    "client_specific_name",
  ];
  const filteredUsers = data
    .filter((logical) =>
      Object.entries(filters).every(([key, value]) =>
        logical[key].toString().toLowerCase().includes(value.toLowerCase()),
      ),
    )
    .filter((logical) =>
      searchTerm.trim() === ""
        ? true
        : Object.values(logical)
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
    );
  const handleToggleFlag = async (lg_id, env_id, app_id, active_flag) => {
    try {
      const newFlag = active_flag === 1 ? 0 : 1;

      await apiService.put(`/logical-groups/${lg_id}`, {
        lg_id,
        env_id,
        app_id,
        active_flag: newFlag,
      });

      fetchLogicalGroups();

      // Show success message
      const message =
        newFlag === 1 ? "Logical Group Activated" : "Logical Group Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      setErrorMessage("Failed to toggle active flag. Please try again.");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Logical Groups Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Logical Groups
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // Reset to first page when searching
              }}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={handleDialogOpen}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "Bold",
                }}
              >
                {columnLabels[col] || col}
              </Typography>

              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#2DB0E4",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>

                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempFilters[col]}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={13} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((lg) => (
                      <TableRow key={lg.lg_id}>
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(
                                  lg.lg_id,
                                  lg.env_id,
                                  lg.app_id,
                                  parseInt(lg.active_flag),
                                )
                              }
                              title="Toggle Active Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    parseInt(lg.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(lg)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteClick(
                                  lg.lg_id,
                                  lg.env_id,
                                  lg.app_id,
                                )
                              }
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>

                        {visibleColumns.client_name && (
                          <TableCell>{lg.client_name}</TableCell>
                        )}
                        {visibleColumns.environment_name && (
                          <TableCell>{lg.environment_name}</TableCell>
                        )}
                        {visibleColumns.application_name && (
                          <TableCell>{lg.application_name}</TableCell>
                        )}
                        {visibleColumns.lg_id && (
                          <TableCell>{lg.lg_id}</TableCell>
                        )}
                        {visibleColumns.name && (
                          <TableCell>{lg.name}</TableCell>
                        )}
                        {visibleColumns.client_specific_name && (
                          <TableCell>{lg.client_specific_name}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={13} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No Logical Groups found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />

          <Dialog
            open={openDialog}
            // onClose={handleDialogClose}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDialog(false);
            }}
            width="small"
            maxWidth="md"
          >
            <DialogTitle
              sx={{
                fontWeight: "700",
                fontSize: "16px",
                py: 1,
              }}
            >
              {isEditing ? "Edit Logical Group" : "Add Logical Group"}
              <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 1.1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                <FormControl fullWidth margin="none" error={!!formErrors.cl_id}>
                  <TextField
                    fullWidth
                    label="Client"
                    value={
                      formData.client_name ||
                      clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                      ""
                    }
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                       minWidth: 300,
                      backgroundColor: "white",

                      "& .MuiInputBase-input": {
                        cursor: "not-allowed",
                        color: "grey",
                      },
                      "& .MuiInputBase-root": {
                        backgroundColor: "white",
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                    margin="dense"
                  />
                </FormControl>
                <FormControl
                  fullWidth
                  margin="none"
                  error={!!formErrors.env_id}
                >
                  <Autocomplete
                    id="env_id"
                    options={environments}
                    getOptionLabel={(option) => option.name || ""} // Display environment name
                    value={
                      environments.find(
                        (environment) => environment.env_id === formData.env_id,
                      ) || null
                    } // Match selected environment
                    onChange={handleEnvironmentChange}
                    ListboxProps={{
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          minHeight: "32px",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Environment"
                        error={!!formErrors.env_id}
                        required
                        helperText={formErrors.env_id}
                      />
                    )}
                    disabled={environments.length === 0}
                  />
                </FormControl>

                <FormControl
                  fullWidth
                  margin="none"
                  error={!!formErrors.app_id}
                >
                  <Autocomplete
                    id="app_id"
                    options={applications}
                    getOptionLabel={(option) => option.name || ""} // Display application name
                    value={
                      applications.find(
                        (application) => application.app_id === formData.app_id,
                      ) || null
                    } // Match selected application
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        app_id: newValue ? newValue.app_id : "",
                      }));
                      setFormErrors((prev) => ({ ...prev, app_id: "" }));
                    }}
                    ListboxProps={{
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          minHeight: "32px",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Application"
                        error={!!formErrors.app_id}
                        required
                        helperText={formErrors.app_id || !formData.env_id}
                      />
                    )}
                    disabled={!formData.env_id || applications.length === 0}
                    noOptionsText={
                      !formData.env_id
                        ? "Please select an environment first"
                        : "No applications available"
                    }
                  />
                </FormControl>

                <TextField
                  fullWidth
                  label="Logical Group ID"
                  name="lg_id"
                  value={formData.lg_id}
                  onChange={handleChange}
                  required
                  error={!!formErrors.lg_id}
                  helperText={formErrors.lg_id}
                  margin="normal"
                  disabled={isEditing}
                />
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Client Specific Name"
                  name="client_specific_name"
                  value={formData.client_specific_name}
                  onChange={handleChange}
                  margin="normal"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isEditing && !isFormChanged}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openDeleteDialog}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              cancelDelete();
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <Typography fontSize="0.9rem">
                Are you sure you want to delete Logical Group{" "}
                <strong>
                  {data.find(
                    (lg) =>
                      lg.lg_id === deleteLgId &&
                      lg.env_id === deleteEnvId &&
                      lg.app_id === deleteAppId,
                  )?.name || deleteLgId}
                </strong>{" "}
                ?
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={cancelDelete}
                sx={{
                  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={confirmDelete}
                sx={{
                  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
}

export default LogicalGroups;
